import { BaseAPIClient } from '../client'
// @ts-ignore - crypto-js doesn't have type definitions
import * as CryptoJS from 'crypto-js'
import * as Sentry from "@sentry/browser";

import type {
  IFinsquidBankIDLoginRequest,
  IFinsquidBankIDLoginResponse,
} from './types'
import { FINSQUID_STORAGE_KEY, type IFinsquidStorage } from './storage'
import { BrowserStorage } from '@/stores/browser'
import type { IFinsquidPosition } from './position'
import type { BankAccount, IntegrationAccount, IntegrationProvider, IntegrationUserAccount, ICompleteIntegrationPosition } from 'findef'
import { FindexClient } from '../findex';

const API_URL = 'https://gateway.finsquid.io/v1/'

//const urlFor = (endpoint: string) => {
//  return API_URL + endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
//}

export interface IFinsquidConfig {}

class FinsquidClient {
  config: IFinsquidConfig
  client: BaseAPIClient
  token?: string
  sid?: string

  constructor(config: IFinsquidConfig) {
    this.config = config
    this.client = new BaseAPIClient(API_URL, undefined, {
      'Content-Type': 'application/json'
    }, undefined, true);

    const storageData = BrowserStorage.getItem<IFinsquidStorage>(FINSQUID_STORAGE_KEY)
    if (storageData) {
      this.sid = storageData.sid
    }
  }

  getSID(forceRefresh?: boolean): string | undefined {
    if (this.sid && !forceRefresh) return this.sid
    const storageData = BrowserStorage.getItem<IFinsquidStorage>(FINSQUID_STORAGE_KEY)
    if (!storageData) return undefined
    this.sid = this.sid || storageData.sid
    return this.sid
  }

  getSelectedProvider(): IntegrationProvider | undefined {
    const storageData = BrowserStorage.getItem<IFinsquidStorage>(FINSQUID_STORAGE_KEY)
    if (!storageData) return undefined
    return storageData.provider
  }

  rememberSID(sid?: string) {
    this.sid = this.sid || sid
    BrowserStorage.patchItem<IFinsquidStorage>(FINSQUID_STORAGE_KEY, { sid: sid })
    return this.sid
  }

  setSID(sid?: string) {
    return this.rememberSID(sid)
  }

  hasSession(): boolean {
    return !!this.sid
  }

  clearCache() {
    this.sid = undefined
    BrowserStorage.patchItem<IFinsquidStorage>(FINSQUID_STORAGE_KEY, { sid: undefined })
  }

  private captureException(e: unknown, endpoint: string, extra?: Record<string, unknown>) {
    Sentry.captureException(e, {
      contexts: {
        finsquid: {
          endpoint,
          sid: this.sid,
          ...extra
        }
      }
    });
  }

  private handleError(e: unknown, endpoint: string, extra?: Record<string, unknown>): never {
    // Check if error is 401 Unauthorized (expired session)
    const errorMessage = e instanceof Error ? e.message : String(e);
    const isUnauthorized = errorMessage.includes('401') ||
                          errorMessage.toLowerCase().includes('unauthorized');

    if (isUnauthorized && this.sid) {
      console.warn(`[Finsquid] Session expired (401) on ${endpoint}, clearing SID from browser storage`);
      this.clearCache();
    }

    this.captureException(e, endpoint, extra);
    throw e;
  }

  async refreshToken() {
    this.client.apiKey = undefined;
    this.token = undefined;
    const token = this.token = await this.getToken();
    this.client.setApiKey(token)
//    console.log({ TOKEN: this.token, API_KEY: this.client.apiKey })
  }

  async getHeaders(): Promise<HeadersInit> {
    return {}
  }

  async getToken(): Promise<string> {
    if (this.token) return this.token;
    const [resp] = await FindexClient.getFinsquidToken();
    if (!resp || !resp.token) throw new Error('Could not fetch token.');
    return this.token = resp.token;

    // return (this.token =
   //   this.token || (await this.client.get<{ token: string }>('token/create')).token)
  }

  /*
    This function should be avoided.
    You need to fetch providers from the findex backend,
    otherwise the providers won't be saved in our database.

    The findex backend requests the providers from finsquid and then saves them
    in our database.
  */
  async getProviders(): Promise<IntegrationProvider[]> {
    // await this.prepare()
    await this.refreshToken()

    try {
      const resp = await this.client.get<IntegrationProvider[]>('providers')
      return resp
    } catch (e) {
      this.handleError(e, 'providers');
    }
  }

  async authBankIDInit(
    providerId: number,
    data: IFinsquidBankIDLoginRequest
  ): Promise<IFinsquidBankIDLoginResponse> {
    await this.refreshToken()
    // await this.prepare()

    let sid: string | undefined = undefined

    try {
      const resp = await this.client.post<IFinsquidBankIDLoginResponse>(
        `login/init`,
        {
          providerId,
          externalUserId: data.userId,
          loginOption: data.loginOption || {
            loginMethod: 'bankid',
            sameDevice: data.sameDevice
          }
        },
        (rawResp) => {
          sid = rawResp.headers.get('sid') || undefined
        }
      )
      resp.sid = this.rememberSID(sid)
      return resp
    } catch (e) {
      this.handleError(e, 'login/init', { providerId, loginMethod: data.loginOption?.loginMethod });
    }
  }

  async authBankID(
    provider: string,
    data: IFinsquidBankIDLoginRequest
  ): Promise<IFinsquidBankIDLoginResponse> {
    // await this.prepare()
    await this.refreshToken()

    try {
      let sid: string | undefined = undefined
      const resp = await this.client.post<IFinsquidBankIDLoginResponse>(
        `login/${provider}/bankid/init`,
        data,
        (rawResp) => {
          sid = rawResp.headers.get('sid') || undefined
        }
      )

      resp.sid = this.rememberSID(sid)
      return resp
    } catch (e) {
      this.handleError(e, `login/${provider}/bankid/init`, { provider });
    }
  }

  async cancelAuthBankId(sid: string = this.sid || '') {
    // await this.prepare()
    await this.refreshToken()

    try {
      return await this.client.get<IFinsquidBankIDLoginResponse>('login/cancel', { sid: sid || '' })
    } catch (e) {
      this.handleError(e, 'login/cancel');
    }
  }

  async getAuthBankIDStatus(sameDevice: boolean = false): Promise<IFinsquidBankIDLoginResponse> {
    await this.refreshToken()

    if (!this.sid) {
      throw new Error('No SID.')
    }

    const params = new URLSearchParams({ includeRawData: 'true', sameDevice: String(sameDevice) }).toString();

    try {
      const resp = await this.client.get<IFinsquidBankIDLoginResponse>(`login/status?${params}`, {
        sid: this.sid
      })
      resp.sid = this.rememberSID(resp.sid || this.sid)
      return resp
    } catch (e) {
      this.handleError(e, 'login/status');
    }
  }

  async getAccounts(): Promise<BankAccount[]> {
    // await this.prepare()
    await this.refreshToken()

    if (!this.sid) {
      throw new Error('No SID.')
    }

    try {
      const accounts = (
        await this.client.get<{ accounts: IntegrationAccount[] }>('accounts', { sid: this.sid })
      ).accounts

      return Promise.all(
        accounts.map(async (acc) => {
          const positions = acc.providerAccountId ? await this.getPositions(acc.providerAccountId) : []
          return {
            ...acc,
            positions
          }
        })
      ) as unknown as BankAccount[];
    } catch (e) {
      this.handleError(e, 'accounts');
    }
  }

  async getAllPositions(accounts?: BankAccount[]): Promise<ICompleteIntegrationPosition[]> {
    // await this.prepare()
    await this.refreshToken()

    if (!this.sid) {
      throw new Error('No SID.')
    }
    accounts = accounts && accounts.length > 0 ? accounts : await this.getAccounts()
    return accounts.flatMap((acc) => acc.positions).flat()
  }

  async getUserAccounts(mock: boolean = false): Promise<IntegrationUserAccount[]> {
    // await this.prepare()
    await this.refreshToken()

    if (!this.sid) throw new Error('Not authenticated.')
    try {
      if (mock) {
        return [
          {
            accountId: '123',
            accountName: 'Account 1'
          },
          {
            accountId: '456',
            accountName: 'Account 2'
          },
          {
            accountId: '789',
            accountName: 'Account 3'
          },
          {
            accountId: '101112',
            accountName: 'Account 4'
          },
        ]
      }
      return await this.client.get<IntegrationUserAccount[]>('login/useraccounts', { sid: this.sid })
    } catch (e) {
      this.handleError(e, 'login/useraccounts');
    }
  }

  async getUserAccount(id: string): Promise<IntegrationUserAccount> {
    // await this.prepare();
    await this.refreshToken()
    if (!this.sid) throw new Error('Not authenticated.');
    const accounts = await this.getUserAccounts();
    const account = accounts.find(it => it.accountId === id);
    if (!account) throw new Error(`No such account: ${id}`);
    return account;
  }

  async selectUserAccount(accountId: string): Promise<any> {
    // await this.prepare()
    await this.refreshToken()

    if (!this.sid) throw new Error('Not authenticated.')
    try {
      return await this.client.get<IntegrationUserAccount[]>(`login/useraccounts/select/${accountId}`, {
        sid: this.sid
      })
    } catch (e) {
      this.handleError(e, 'login/useraccounts/select');
    }
  }

  async selectFirstUserAccount(): Promise<void> {
    // await this.prepare()
    await this.refreshToken()

    const sid = this.getSID()
    if (!sid) throw new Error('Not authenticated.')

    const accounts = await this.getUserAccounts()

    for (const account of accounts) {
      await this.selectUserAccount(account.accountId)
      break
    }
  }

  async alive(sid?: string): Promise<boolean> {
    try {
      // await this.prepare()
      await this.refreshToken()
      sid = sid || this.getSID()
      if (!sid) return false
      const resp = await this.client.get<{ status: string }>('login/alive', { sid: sid })
      return resp.status === 'complete'
    } catch (e) {
      return false
    }
  }

  async getPositions(accountId: string): Promise<IFinsquidPosition[]> {
    // await this.prepare()
    await this.refreshToken()

    if (!this.sid) {
      throw new Error('No SID.')
    }
    try {
      return await this.client.get<IFinsquidPosition[]>(`accounts/${accountId}/positions`, {
        sid: this.sid
      })
    } catch (e) {
      console.warn(e)
      // Check if 401 and clear SID
      const errorMessage = e instanceof Error ? e.message : String(e);
      const isUnauthorized = errorMessage.includes('401') || errorMessage.toLowerCase().includes('unauthorized');
      if (isUnauthorized && this.sid) {
        console.warn(`[Finsquid] Session expired (401) on accounts/positions, clearing SID from browser storage`);
        this.clearCache();
      }
      this.captureException(e, 'accounts/positions', { accountId });
      return []
    }
  }

  getQRCode(challenge: string, orderTime: number = new Date().getTime() / 1000): string {
    const generateQRCodeData = (qrStartToken: string, qrStartSecret: string) => {
      //const orderTime = new Date().getTime() / 1000;
      const qrTime = String(Math.floor(new Date().getTime() / 1000 - orderTime))
      const qrAuthCode = CryptoJS.HmacSHA256(qrTime, qrStartSecret).toString(CryptoJS.enc.Hex)
      return `bankid.${qrStartToken}.${qrTime}.${qrAuthCode}`
    }
    //
    const parts = challenge.split('.')
    if (parts.length === 4) {
      const start = parts[1]
      const end = parts[3]
      return generateQRCodeData(start, end)
    }

    return ''
  }
}

export const finsquid = new FinsquidClient({})
